import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  ViewColumnsIcon,
  SwatchIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import { useNavPermitida } from './navConfig';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface ItemPalette {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  executar: () => void;
}

interface SecaoPalette {
  label: string;
  itens: ItemPalette[];
}

// Command Palette (⌘K/Ctrl+K) — navegação rápida + ações. Lista APENAS telas
// permitidas (mesmo registro filtrado do Sidebar via useNavPermitida).
const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme, density, setDensity } = useTheme();
  const grupos = useNavPermitida();

  const [query, setQuery] = useState('');
  const [indice, setIndice] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndice(0);
      // autofocus após o mount do overlay
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const secoes = useMemo<SecaoPalette[]>(() => {
    const acoes: ItemPalette[] = [
      {
        id: 'acao-tema',
        label: theme === 'dark' ? 'Alternar pra tema claro' : 'Alternar pra tema escuro',
        icon: theme === 'dark' ? SunIcon : MoonIcon,
        executar: () => { toggleTheme(); onClose(); },
      },
      {
        id: 'acao-densidade',
        label: density === 'comfortable' ? 'Alternar pra densidade compacta' : 'Alternar pra densidade confortável',
        icon: ViewColumnsIcon,
        executar: () => { setDensity(density === 'comfortable' ? 'compact' : 'comfortable'); onClose(); },
      },
      {
        id: 'acao-aparencia',
        label: 'Abrir Aparência (cor de destaque)',
        icon: SwatchIcon,
        executar: () => { navigate('/config#aparencia'); onClose(); },
      },
    ];

    const deNav: SecaoPalette[] = grupos.map((g) => ({
      label: g.label ?? 'Geral',
      itens: g.itens.map((i) => ({
        id: i.to,
        label: i.label,
        icon: i.icon,
        executar: () => { navigate(i.to); onClose(); },
      })),
    }));

    const todas = [{ label: 'Ações', itens: acoes }, ...deNav];

    const q = query.trim().toLowerCase();
    if (!q) return todas;
    return todas
      .map((s) => ({ ...s, itens: s.itens.filter((i) => i.label.toLowerCase().includes(q)) }))
      .filter((s) => s.itens.length > 0);
  }, [grupos, query, theme, density, toggleTheme, setDensity, navigate, onClose]);

  // Índice global achatado (↑/↓ atravessam as seções).
  const planos = useMemo(() => secoes.flatMap((s) => s.itens), [secoes]);
  const idxSeguro = Math.min(indice, Math.max(0, planos.length - 1));

  useEffect(() => { setIndice(0); }, [query]);

  // Mantém o item ativo visível ao navegar com setas.
  useEffect(() => {
    const el = listaRef.current?.querySelector('[data-ativo="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [idxSeguro]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndice((i) => Math.min(i + 1, planos.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndice((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); planos[idxSeguro]?.executar(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  if (!open) return null;

  let cursor = -1;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-center pt-[12vh]"
      style={{ background: 'var(--scrim)', backdropFilter: 'blur(2px)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="h-fit w-full max-w-[560px] overflow-hidden rounded-iris-lg border border-line bg-surface shadow-iris-xl">
        {/* Input */}
        <div className="flex items-center gap-2.5 border-b border-line px-4">
          <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar ações, telas…"
            className="h-11 w-full bg-transparent text-sm text-tx outline-none placeholder:text-tx-3"
          />
        </div>

        {/* Resultados agrupados */}
        <div ref={listaRef} className="max-h-[50vh] overflow-y-auto p-1.5">
          {planos.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-tx-3">Nada encontrado pra “{query}”.</p>
          )}
          {secoes.map((s) => (
            <div key={s.label}>
              <div className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.04em] text-tx-3">
                {s.label}
              </div>
              {s.itens.map((item) => {
                cursor += 1;
                const ativo = cursor === idxSeguro;
                const Icone = item.icon;
                const meuCursor = cursor;
                return (
                  <button
                    key={item.id}
                    data-ativo={ativo}
                    onMouseEnter={() => setIndice(meuCursor)}
                    onClick={item.executar}
                    className={`flex w-full items-center gap-2.5 rounded-iris-sm px-2.5 py-2 text-left text-sm transition-colors ${
                      ativo ? 'bg-accent-soft text-accent-text' : 'text-tx-2'
                    }`}
                  >
                    <Icone className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {ativo && <ArrowUturnLeftIcon className="h-3.5 w-3.5 flex-shrink-0 -scale-y-100" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Rodapé com dicas */}
        <div className="flex items-center gap-3 border-t border-line px-4 py-2 text-[11px] text-tx-3">
          <span>↑↓ navegar</span>
          <span>↵ selecionar</span>
          <span>esc fechar</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
